import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarClock, Compass, GraduationCap, ShieldCheck, Sparkles, Users } from "lucide-react";

import SEO from "@/components/site/SEO";
import PageHero from "@/components/site/PageHero";
import CTASection from "@/components/site/CTASection";

const TDF_URL = "https://www.traindriverfoundation.com/become-a-train-driver-at-18";

const PILLARS = [
  {
    icon: CalendarClock,
    title: "The 2026 age-change explained",
    body:
      "The minimum age to drive trains on Great Britain's mainline is changing from 20 to 18, effective 30 June 2026. This opens the door for 18- and 19-year-olds to apply — but the assessment standard remains the same. Future Driver helps younger applicants meet that standard from day one.",
  },
  {
    icon: Compass,
    title: "Building operational maturity early",
    body:
      "Operational maturity is what distinguishes a successful candidate from a confident one. We focus on the language, mindset, judgement and rule discipline expected of someone responsible for a moving train — long before assessment day.",
  },
  {
    icon: ShieldCheck,
    title: "Safety-critical thinking from the start",
    body:
      "Safety-critical thinking is not a phase you grow into — it is a habit you build. Future Driver embeds situational awareness, communication discipline and rule following as core habits, so they feel natural by the time you apply.",
  },
  {
    icon: GraduationCap,
    title: "Psychometric readiness, properly",
    body:
      "We align preparation to RIS-3751-TOM through our specialist brand Train Driver Psychometrics — so practice is realistic, not generic. Familiarity with format, timing and skill demands meaningfully improves performance.",
  },
  {
    icon: Sparkles,
    title: "Application & interview craft",
    body:
      "Younger applicants are often discounted because their evidence does not yet match the role's language. We help structure your evidence: reliability, calm decision making under pressure, attention to detail, learning from mistakes, and safety mindset.",
  },
  {
    icon: Users,
    title: "Built with the industry in mind",
    body:
      "Future Driver is informed by how UK operators actually recruit. It does not replace employer training, formal competence assessment, medicals or licensing — but it gets you to the start line ready.",
  },
];

const TIMELINE = [
  {
    when: "Age 16–17",
    title: "Discover the role",
    body: "Use Railway Careers UK to understand operators, roles, pathways and operational language.",
  },
  {
    when: "Age 17–18",
    title: "Build operational maturity",
    body: "Develop the language, mindset and habits of a safety-critical professional through structured Future Driver preparation.",
  },
  {
    when: "Age 18+",
    title: "Practise to the standard",
    body: "Train Driver Psychometrics provides RIS-3751-TOM aligned practice; Train Driver Foundation prepares your CV, interview and operational thinking.",
  },
  {
    when: "Apply",
    title: "Operator recruitment",
    body: "When 2026 rules take effect, apply directly through the operator. Your preparation should make you a credible candidate from the first sift.",
  },
];

export default function FutureDriver() {
  return (
    <>
      <SEO
        title="Future Driver | Become a train driver at 18 — UKTDA preparation pathway"
        description="From 30 June 2026 you can apply to become a UK train driver at 18. UKTDA's Future Driver pathway helps younger applicants build operational maturity, safety-critical thinking and psychometric readiness."
        path="/future-driver"
      />

      <PageHero
        eyebrow="Future Driver"
        title="Become a train driver at 18 — and be ready when 2026 changes the rules."
        intro="From 30 June 2026, the minimum age to drive on Great Britain's mainline is changing to 18. Future Driver is the UKTDA preparation pathway for younger applicants — built around operational maturity, safety-critical thinking and psychometric readiness."
      >
        <div className="flex flex-wrap gap-3">
          <a
            href={TDF_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="future-driver-tdf-cta"
            className="inline-flex items-center gap-2 bg-safety hover:bg-[#e66e00] text-white font-semibold px-5 py-3 rounded-md transition-colors"
          >
            Start with Train Driver Foundation <ArrowUpRight className="h-4 w-4" />
          </a>
          <Link
            to="/contact"
            data-testid="future-driver-contact-cta"
            className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/5 text-white font-semibold px-5 py-3 rounded-md transition-colors"
          >
            Talk to UKTDA
          </Link>
        </div>
      </PageHero>

      {/* Pillars */}
      <section className="py-20 md:py-28 bg-white" data-testid="future-driver-pillars">
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">What Future Driver covers</p>
            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl lg:text-5xl text-navy leading-tight text-balance">
              Six pillars that turn a young applicant into a credible trainee driver candidate.
            </h2>
          </div>
          <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {PILLARS.map((p, i) => (
              <article
                key={p.title}
                className="bg-white border border-slate-200 rounded-md p-6 hover:border-safety/60 hover:shadow-sm transition-all"
                data-testid={`future-driver-pillar-${i}`}
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-navy text-white">
                  <p.icon className="h-5 w-5" />
                </div>
                <p className="mt-4 font-display font-bold text-navy text-lg">{p.title}</p>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-200" data-testid="future-driver-timeline">
        <div className="container-rail">
          <p className="label-eyebrow text-safety">Your pathway</p>
          <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight max-w-2xl">
            From curiosity to credible candidate.
          </h2>

          <ol className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {TIMELINE.map((t, i) => (
              <li
                key={t.when}
                className="bg-white border border-slate-200 rounded-md p-6 relative"
                data-testid={`future-driver-step-${i}`}
              >
                <span className="font-mono-ibm text-[11px] tracking-widest text-slate-400">
                  STEP {String(i + 1).padStart(2, "0")}
                </span>
                <p className="mt-2 label-eyebrow text-safety">{t.when}</p>
                <p className="mt-3 font-display font-bold text-navy text-lg">{t.title}</p>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* TDF banner */}
      <section className="py-20 md:py-24 bg-white" data-testid="future-driver-tdf-banner">
        <div className="container-rail">
          <div className="bg-navy-deep text-white rounded-md p-8 md:p-12 grid lg:grid-cols-[3fr_2fr] gap-10 items-center">
            <div>
              <p className="label-eyebrow text-safety">Specialist brand</p>
              <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl text-white leading-tight">
                Become a train driver at 18 — with Train Driver Foundation.
              </h2>
              <p className="mt-4 text-slate-300 leading-relaxed max-w-2xl">
                Train Driver Foundation runs the dedicated Future Driver candidate programme — covering CV craft, interview practice, operational thinking, and the realities of trainee driver assessment for younger applicants.
              </p>
              <a
                href={TDF_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="future-driver-tdf-banner-link"
                className="mt-6 inline-flex items-center gap-2 bg-safety hover:bg-[#e66e00] text-white font-semibold px-5 py-3 rounded-md transition-colors"
              >
                Become a train driver at 18 <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
            <ul className="space-y-3 text-sm text-slate-200">
              {[
                "Designed around the 2026 age change",
                "Structured CV and interview craft",
                "Operational thinking and rule discipline",
                "Psychometric familiarisation via TDP",
                "Built around real operator expectations",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 bg-safety rounded-sm" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>


        </div>
      </section>

      <CTASection
        eyebrow="Get started"
        title="Talk to us about your route into the cab."
        intro="Whether you are 16, 18 or 22 — preparation makes the difference. We will point you to the right UKTDA brand for where you are now."
        primary={{ label: "Contact UKTDA", to: "/contact", testId: "future-driver-cta-contact" }}
        secondary={{ label: "For individuals", to: "/for-individuals", testId: "future-driver-cta-individuals" }}
      />
    </>
  );
}
